import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { lessonsApi } from "@/api";
import type { CreateLessonData, Lesson, UpdateLessonData } from "@/api";
import { QUERY_KEYS } from "@/constants";

/** Bo'limning darslari (tartiblangan). */
export const useLessons = (sectionId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_LESSONS, sectionId],
    queryFn: () => lessonsApi.getBySection(sectionId as number),
    enabled: !!sectionId,
    select: res => res.data,
  });

/**
 * Bir nechta bo'limning darslari BIRDANIGA — kursni boshidan oxirigacha
 * ko'rib chiqish uchun. Kalit `useLessons` niki bilan bir xil: kesh
 * umumiy, muharrirdagi o'zgarish shu yerda ham yangilanadi.
 */
export const useSectionsLessons = (sectionIds: number[]) => {
  const queries = useQueries({
    queries: sectionIds.map(sectionId => ({
      queryKey: [QUERY_KEYS.GET_LESSONS, sectionId],
      queryFn: () => lessonsApi.getBySection(sectionId),
      select: (res: Awaited<ReturnType<typeof lessonsApi.getBySection>>) => res.data,
    })),
  });

  const bySection = new Map<number, Lesson[]>(
    sectionIds.map((sectionId, index) => [sectionId, queries[index]?.data ?? []]),
  );

  return {
    bySection,
    isLoading: queries.some(query => query.isLoading),
    isError: queries.some(query => query.isError),
  };
};

export const useLesson = (id: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_LESSON, id],
    queryFn: () => lessonsApi.getById(id as number),
    enabled: !!id,
    select: res => res.data,
  });

/**
 * Dars o'zgargach eskiradigan hamma narsa: bo'lim ro'yxati, bo'limlar
 * ro'yxati (dars soni ko'rsatiladi) va kursning nashrga tayyorlik holati.
 */
const useInvalidateLessons = (sectionId: number, courseId?: number) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LESSONS, sectionId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SECTIONS, courseId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_READINESS, courseId] });
  };
};

export const useCreateLesson = (sectionId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateLessons(sectionId, courseId);
  return useMutation({
    mutationFn: (data: CreateLessonData) => lessonsApi.create(data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdateLesson = (sectionId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateLessons(sectionId, courseId);
  return useMutation({
    mutationFn: (vars: { id: number; data: UpdateLessonData }) =>
      lessonsApi.update(vars.id, vars.data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeleteLesson = (sectionId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateLessons(sectionId, courseId);
  return useMutation({
    mutationFn: (id: number) => lessonsApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.DELETED"));
    },
  });
};

/** `lessonIds` — bo'limdagi BARCHA darslar yangi tartibda. */
export const useReorderLessons = (sectionId: number, courseId?: number) => {
  const invalidate = useInvalidateLessons(sectionId, courseId);
  return useMutation({
    mutationFn: (lessonIds: number[]) => lessonsApi.reorder(lessonIds),
    onSuccess: invalidate,
  });
};

export const useToggleLessonPublish = (sectionId: number, courseId?: number) => {
  const invalidate = useInvalidateLessons(sectionId, courseId);
  return useMutation({
    mutationFn: (id: number) => lessonsApi.togglePublish(id),
    onSuccess: res => {
      invalidate();
      if (res.message) toast.success(res.message);
    },
  });
};

export const useDuplicateLesson = (sectionId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateLessons(sectionId, courseId);
  return useMutation({
    mutationFn: (id: number) => lessonsApi.duplicate(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};
